/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { $UserFilters, GetUsersData, GetUsersResponse, User, UserFilters, UserSortableFields } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedMembersQueryParams = {
  params?: Omit<GetUsersData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
  urlPrefix?: string;
};

/**
 * Custom hook that fetches paginated users with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated users response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedMembersQuery({ params, type, urlPrefix }: UsePaginatedMembersQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<GetUsersResponse, User, UserFilters, UserSortableFields, GetUsersData>({
    apiCall: mgmtPlaneAPIClient.users.getUsers.bind(mgmtPlaneAPIClient.users),
    filterTypes: jsonSchemaToTypeMap($UserFilters),
    urlPrefix,
    queryKey: 'users',
    realtimeEvents: ['user:created', 'user:updated'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
